import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DealFilters = ({ filters, onFiltersChange, onClearFilters }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const dealTypes = [
    { id: 'all', label: 'All Deals', count: 156 },
    { id: 'flash', label: 'Flash Sales', count: 23 },
    { id: 'seasonal', label: 'Seasonal', count: 45 },
    { id: 'clearance', label: 'Clearance', count: 67 },
    { id: 'exclusive', label: 'Exclusive', count: 21 }
  ];

  const priceRanges = [
    { id: 'all', label: 'Any Price', min: 0, max: 10000 },
    { id: 'under-100', label: 'Under $100', min: 0, max: 100 },
    { id: '100-300', label: '$100 - $300', min: 100, max: 300 },
    { id: '300-500', label: '$300 - $500', min: 300, max: 500 },
    { id: 'over-500', label: 'Over $500', min: 500, max: 10000 }
  ];

  const discountRanges = [
    { id: 'all', label: 'Any Discount' },
    { id: '10-25', label: '10% - 25%' },
    { id: '25-50', label: '25% - 50%' },
    { id: '50-plus', label: '50%+' }
  ];

  const brands = [
    'Dyson', 'Shark', 'Bissell', 'Hoover', 'Miele', 'Tineco', 'Black+Decker', 'Eureka'
  ];

  const stores = [
    'Amazon', 'Best Buy', 'Target', 'Walmart', 'Home Depot', 'Lowes', 'Costco', 'Wayfair'
  ];

  const handleFilterChange = (filterType, value) => {
    onFiltersChange({
      ...filters,
      [filterType]: value
    });
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-text-primary">Filter Deals</h2>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="text-muted-foreground hover:text-destructive"
          >
            Clear All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleExpanded}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            className="lg:hidden"
          >
            {isExpanded ? 'Less' : 'More'}
          </Button>
        </div>
      </div>
      {/* Search */}
      <div className="mb-6">
        <Input
          type="search"
          placeholder="Search deals..."
          value={filters?.search || ''}
          onChange={(e) => handleFilterChange('search', e?.target?.value)}
          className="w-full"
        />
      </div>
      {/* Always Visible Filters */}
      <div className="space-y-6">
        {/* Deal Type */}
        <div>
          <h3 className="font-medium text-text-primary mb-3">Deal Type</h3>
          <div className="space-y-2">
            {dealTypes?.map((type) => (
              <label key={type?.id} className="flex items-center space-x-3 cursor-pointer group">
                <input
                  type="radio"
                  name="dealType"
                  value={type?.id}
                  checked={filters?.dealType === type?.id}
                  onChange={(e) => handleFilterChange('dealType', e?.target?.value)}
                  className="w-4 h-4 text-primary border-border focus:ring-primary focus:ring-2"
                />
                <span className="text-sm text-text-primary group-hover:text-primary transition-colors flex-1">
                  {type?.label}
                </span>
                <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                  {type?.count}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Price Range */}
        <div>
          <h3 className="font-medium text-text-primary mb-3">Price Range</h3>
          <div className="space-y-2">
            {priceRanges?.map((range) => (
              <label key={range?.id} className="flex items-center space-x-3 cursor-pointer group">
                <input
                  type="radio"
                  name="priceRange"
                  value={range?.id}
                  checked={filters?.priceRange === range?.id}
                  onChange={(e) => handleFilterChange('priceRange', e?.target?.value)}
                  className="w-4 h-4 text-primary border-border focus:ring-primary focus:ring-2"
                />
                <span className="text-sm text-text-primary group-hover:text-primary transition-colors">
                  {range?.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>
      {/* Expandable Filters */}
      <div className={`mt-6 space-y-6 ${isExpanded ? 'block' : 'hidden lg:block'}`}>
        {/* Discount Range */}
        <div>
          <h3 className="font-medium text-text-primary mb-3">Discount</h3>
          <div className="space-y-2">
            {discountRanges?.map((range) => (
              <label key={range?.id} className="flex items-center space-x-3 cursor-pointer group">
                <input
                  type="radio"
                  name="discountRange"
                  value={range?.id}
                  checked={filters?.discountRange === range?.id}
                  onChange={(e) => handleFilterChange('discountRange', e?.target?.value)}
                  className="w-4 h-4 text-primary border-border focus:ring-primary focus:ring-2"
                />
                <span className="text-sm text-text-primary group-hover:text-primary transition-colors">
                  {range?.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Brands */}
        <div>
          <h3 className="font-medium text-text-primary mb-3">Brand</h3>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {brands?.map((brand) => (
              <label key={brand} className="flex items-center space-x-3 cursor-pointer group">
                <input
                  type="checkbox"
                  value={brand}
                  checked={filters?.brands?.includes(brand) || false}
                  onChange={(e) => {
                    const currentBrands = filters?.brands || [];
                    const newBrands = e?.target?.checked
                      ? [...currentBrands, brand]
                      : currentBrands?.filter(b => b !== brand);
                    handleFilterChange('brands', newBrands);
                  }}
                  className="w-4 h-4 text-primary border-border focus:ring-primary focus:ring-2 rounded"
                />
                <span className="text-sm text-text-primary group-hover:text-primary transition-colors">
                  {brand}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Stores */}
        <div>
          <h3 className="font-medium text-text-primary mb-3">Store</h3>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {stores?.map((store) => (
              <label key={store} className="flex items-center space-x-3 cursor-pointer group">
                <input
                  type="checkbox"
                  value={store}
                  checked={filters?.stores?.includes(store) || false}
                  onChange={(e) => {
                    const currentStores = filters?.stores || [];
                    const newStores = e?.target?.checked
                      ? [...currentStores, store]
                      : currentStores?.filter(s => s !== store);
                    handleFilterChange('stores', newStores);
                  }}
                  className="w-4 h-4 text-primary border-border focus:ring-primary focus:ring-2 rounded"
                />
                <span className="text-sm text-text-primary group-hover:text-primary transition-colors">
                  {store}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Additional Options */}
        <div>
          <h3 className="font-medium text-text-primary mb-3">Additional Options</h3>
          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={filters?.freeShipping || false}
                onChange={(e) => handleFilterChange('freeShipping', e?.target?.checked)}
                className="w-4 h-4 text-primary border-border focus:ring-primary focus:ring-2 rounded"
              />
              <span className="text-sm text-text-primary group-hover:text-primary transition-colors">
                Free Shipping Only
              </span>
            </label>
            <label className="flex items-center space-x-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={filters?.exclusiveDeals || false}
                onChange={(e) => handleFilterChange('exclusiveDeals', e?.target?.checked)}
                className="w-4 h-4 text-primary border-border focus:ring-primary focus:ring-2 rounded"
              />
              <span className="text-sm text-text-primary group-hover:text-primary transition-colors">
                Exclusive Deals Only
              </span>
            </label>
            <label className="flex items-center space-x-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={filters?.endingSoon || false}
                onChange={(e) => handleFilterChange('endingSoon', e?.target?.checked)}
                className="w-4 h-4 text-primary border-border focus:ring-primary focus:ring-2 rounded"
              />
              <span className="text-sm text-text-primary group-hover:text-primary transition-colors">
                Ending Soon (24h)
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealFilters;